import { useCallback } from 'react';

import { prepareValuesForSubmission } from 'helpers/candidate_application/form-page-helpers';
import { useDispatch, useSelector } from 'react-redux';

import {
  saveCandidateChecksValues,
  selectCandidateApplication,
  updateCandidateValues,
  updateFormValues,
} from 'api/candidate-application-slice';

/**
 * Hook for preparing form values for submission
 */
export default function useFormSubmission() {
  const dispatch = useDispatch();
  const {
    candidateFields,
    application: { check_values },
  } = useSelector(selectCandidateApplication);
  const {
    sections = [],
    candidateValues = {},
    hiddenValues = {},
    formValues = {},
    sectionFieldPrefixes = {},
    fieldPathIndex = {},
    excludeOnSubmitCodes = [],
  } = candidateFields || {};

  const prepareForSubmission = useCallback(
    (watchValues, sectionKey, currentSubsectionPrefixes = null, existingValues = {}) => {
      return prepareValuesForSubmission(
        watchValues,
        candidateValues,
        hiddenValues,
        formValues, // Redux formValues from state
        sections,
        sectionKey,
        fieldPathIndex,
        excludeOnSubmitCodes,
        currentSubsectionPrefixes,
        sectionFieldPrefixes,
        existingValues,
      );
    },
    [
      sections,
      candidateValues,
      hiddenValues,
      formValues,
      sectionFieldPrefixes,
      fieldPathIndex,
      excludeOnSubmitCodes,
    ],
  );

  const saveFormValuesAndSubmit = useCallback(
    async (
      sectionKey,
      watchValues,
      token,
      systemValues = {},
      currentSubsectionPrefixes = null,
      rejectedValuesFix = false,
    ) => {
      // Use the comprehensive submission process
      // prepareValuesForSubmission now handles preserving values from completed checks/extracted fields
      const existingValues = check_values?.values || {};
      const { updatedCandidateValues, updatedSectionValues } = prepareValuesForSubmission(
        watchValues,
        candidateValues,
        hiddenValues,
        formValues, // Redux formValues from state
        sections,
        sectionKey,
        fieldPathIndex,
        excludeOnSubmitCodes,
        currentSubsectionPrefixes,
        sectionFieldPrefixes,
        existingValues,
      );

      let updateRejectValues = null;
      let rejectedStatus = false;
      if (rejectedValuesFix) {
        updateRejectValues = check_values.rejected_values?.values?.filter(
          value => !(value in watchValues),
        );
        rejectedStatus =
          updateRejectValues?.length > 0 || check_values.rejected_values?.files?.length > 0
            ? true
            : false;
      }

      // Save to backend
      await dispatch(
        saveCandidateChecksValues({
          token,
          data: {
            values: updatedCandidateValues,
            system_values: systemValues,
            ...(rejectedValuesFix
              ? {
                  rejected_fixes: { ...check_values.rejected_fixes, values: true },
                  ...(updateRejectValues
                    ? {
                        rejected_values: {
                          ...check_values.rejected_values,
                          values: updateRejectValues,
                        },
                      }
                    : {}),
                  rejected: rejectedStatus,
                }
              : {}),
          },
        }),
      );

      dispatch(updateCandidateValues({ updatedCandidateValues }));

      dispatch(updateFormValues({ sectionKey, formValues: updatedSectionValues }));
    },
    [
      dispatch,
      sections,
      candidateValues,
      hiddenValues,
      formValues,
      sectionFieldPrefixes,
      fieldPathIndex,
      excludeOnSubmitCodes,
      check_values,
    ],
  );

  return { prepareForSubmission, saveFormValuesAndSubmit };
}
